import './style.css'
import { createLibp2p } from 'libp2p'
import { webSockets } from '@libp2p/websockets'
import { noise } from '@libp2p/noise'
import { yamux } from '@libp2p/yamux'
import { gossipsub } from '@libp2p/gossipsub'
import { identify } from '@libp2p/identify'
import { bootstrap } from '@libp2p/bootstrap'
import { multiaddr } from '@multiformats/multiaddr'

const DISCOVERY_TOPIC = 'lpman-room-discovery'
const ROOM_PREFIX = 'lpman-room-'
const BOOTSTRAP_RESOLVE_URL = (import.meta.env.VITE_BOOTSTRAP_RESOLVE_URL || '/api/bootstrap-resolve').trim()
const RTC_CONFIG_URL = (import.meta.env.VITE_RTC_CONFIG_URL || '/api/rtc-config').trim()
const ICE_SERVERS_URL = '/ice-servers.json'
const HISTORY_SYNC_LIMIT = 60
const SESSION_HISTORY_LIMIT = 200
const ROOM_HISTORY_PREFIX = 'lpchat-room-history:'
const ROOM_LAST_SEEN_PREFIX = 'lpchat-room-last-seen:'
const ROOM_PASSPHRASE_PREFIX = 'lpchat-room-passphrase:'
const ROOMS_STATE_KEY = 'lpchat-rooms-state:v1'
const ROOM_PRESENCE_TTL_MS = 45000
const ROOM_EMPTY_GRACE_MS = 120000
const VOICE_SPEAKING_HOLD_MS = 1200
const DISCOVERY_STALE_TIMEOUT_MS = 90000
const DISCOVERY_STALE_MAX_CONSECUTIVE = 3

function normalizeBootstrapAddr(addr) {
  const m = addr.match(/^(.*\/p2p\/([^/]+))\/p2p\/\2$/)
  return m ? m[1] : addr
}

function normalizeIceServer(server) {
  if (!server || typeof server !== 'object') return null
  const rawUrls = server.urls
  const urls = Array.isArray(rawUrls)
    ? rawUrls.map((u) => String(u).trim()).filter(Boolean)
    : typeof rawUrls === 'string'
      ? rawUrls.trim()
      : ''
  if (!urls || (Array.isArray(urls) && urls.length === 0)) return null

  const out = { urls }
  if (typeof server.username === 'string') out.username = server.username
  if (typeof server.credential === 'string') out.credential = server.credential
  if (typeof server.credentialType === 'string') out.credentialType = server.credentialType
  return out
}

async function loadIceServers() {
  state.iceServers = []
  try {
    const apiRes = await fetch(RTC_CONFIG_URL, { cache: 'no-store' })
    if (apiRes.ok) {
      const json = await apiRes.json()
      const list = Array.isArray(json)
        ? json
        : json && Array.isArray(json.iceServers)
          ? json.iceServers
          : []
      const normalized = list.map(normalizeIceServer).filter(Boolean)
      state.iceServers = normalized.length ? normalized : DEFAULT_ICE_SERVERS
      return
    }
  } catch (err) {
    console.warn('Could not load ICE config from runtime API, trying static file:', err)
  }

  try {
    const res = await fetch(ICE_SERVERS_URL, { cache: 'no-store' })
    if (!res.ok) {
      state.iceServers = DEFAULT_ICE_SERVERS
      return
    }
    const json = await res.json()
    const list = Array.isArray(json)
      ? json
      : json && Array.isArray(json.iceServers)
        ? json.iceServers
        : []
    const normalized = list.map(normalizeIceServer).filter(Boolean)
    state.iceServers = normalized.length ? normalized : DEFAULT_ICE_SERVERS
  } catch (err) {
    console.warn('Could not load ICE server config, using defaults:', err)
    state.iceServers = DEFAULT_ICE_SERVERS
  }
}

function normalizeSessionName(name) {
  const value = (name || '').trim().replace(/\s+/g, ' ')
  return value.slice(0, 32)
}

function setPeerName(publicKey, name) {
  if (!publicKey) return
  const normalized = normalizeSessionName(name)
  if (normalized) state.peerNames.set(publicKey, normalized)
}

function getDisplayName(publicKey, fallbackName) {
  const byKey = publicKey ? state.peerNames.get(publicKey) : ''
  const normalizedFallback = normalizeSessionName(fallbackName)
  if (byKey) return byKey
  if (normalizedFallback) return normalizedFallback
  if (!publicKey) return 'unknown'
  return publicKey.slice(0, 16)
}

const state = {
  keyPair: null,
  publicKeyBase64: '',
  sessionName: '',
  peerNames: new Map(),
  node: null,
  rooms: new Map(),
  joined: new Set(),
  roomKeys: new Map(),
  roomPassphrases: new Map(),
  messages: new Map(),
  currentRoomId: null,
  seen: new Set(),
  isOnline: false,
  voiceRoom: null,
  voicePeers: new Map(),
  voicePendingCandidates: new Map(),
  localStream: null,
  iceServers: [],
  bootstrapAliases: new Map(),
  roomLastSeen: new Map(),
  directKeys: new Map(),
  isReconnecting: false,
  hadPeerConnection: false,
  announceIntervalId: null,
  statusIntervalId: null,
  reconnectTimerId: null,
  roomPresence: new Map(),
  voiceSpeakingUntil: new Map(),
  voiceMeters: new Map(),
  voicePanelIntervalId: null,
  lastRemoteDiscoveryAt: 0,
  discoveryStaleChecks: 0,
}

function getRoomPresenceMap(roomId) {
  let map = state.roomPresence.get(roomId)
  if (!map) {
    map = new Map()
    state.roomPresence.set(roomId, map)
  }
  return map
}

function touchRoomPresence(roomId, publicKey, data = {}) {
  if (!roomId || !publicKey) return
  const map = getRoomPresenceMap(roomId)
  const prev = map.get(publicKey) || { publicKey, roomId, voice: false, sessionName: '' }
  const next = {
    ...prev,
    ...data,
    publicKey,
    roomId,
    lastSeen: Date.now(),
  }
  map.set(publicKey, next)
  const room = state.rooms.get(roomId)
  if (room) {
    room.members.add(publicKey)
    room.lastSeen = Date.now()
  }
}

function markSpeaking(publicKey) {
  if (!publicKey) return
  state.voiceSpeakingUntil.set(publicKey, Date.now() + VOICE_SPEAKING_HOLD_MS)
}

function isSpeaking(publicKey, now = Date.now()) {
  const until = state.voiceSpeakingUntil.get(publicKey) || 0
  return until > now
}

function prunePresence() {
  const now = Date.now()
  let roomsChanged = false
  for (const [roomId, map] of state.roomPresence) {
    for (const [publicKey, entry] of map) {
      if (!entry?.lastSeen || now - entry.lastSeen > ROOM_PRESENCE_TTL_MS) {
        map.delete(publicKey)
      }
    }
    const room = state.rooms.get(roomId)
    if (room) {
      const members = new Set([...map.keys()])
      if (state.joined.has(roomId) && state.publicKeyBase64 && state.isOnline && state.node) {
        members.add(state.publicKeyBase64)
      }
      const lastSeen = Number(room.lastSeen) || 0
      const shouldExpireRoom = !state.joined.has(roomId)
        && members.size === 0
        && lastSeen > 0
        && now - lastSeen > ROOM_EMPTY_GRACE_MS
      if (shouldExpireRoom) {
        state.rooms.delete(roomId)
        state.roomPresence.delete(roomId)
        state.messages.delete(roomId)
        state.roomLastSeen.delete(roomId)
        roomsChanged = true
        continue
      }
      room.members = members
    }
  }
  for (const [roomId, room] of state.rooms) {
    if (state.roomPresence.has(roomId)) continue
    const lastSeen = Number(room?.lastSeen) || 0
    const shouldExpireRoom = !state.joined.has(roomId)
      && lastSeen > 0
      && now - lastSeen > ROOM_EMPTY_GRACE_MS
    if (!shouldExpireRoom) continue
    state.rooms.delete(roomId)
    state.messages.delete(roomId)
    state.roomLastSeen.delete(roomId)
    roomsChanged = true
  }
  if (roomsChanged) {
    persistRoomsState()
  }
  return roomsChanged
}

function getActiveRoomMembers(roomId) {
  prunePresence()
  const map = state.roomPresence.get(roomId)
  if (map && map.size) return [...map.keys()]
  const room = state.rooms.get(roomId)
  return room ? [...room.members] : []
}

async function publishRoomPresence(roomId) {
  if (!roomId || !state.node || !state.joined.has(roomId)) return
  touchRoomPresence(roomId, state.publicKeyBase64, {
    sessionName: state.sessionName,
    voice: state.voiceRoom === roomId,
  })
  await publish(ROOM_PREFIX + roomId, {
    type: 'presence',
    roomId,
    publicKey: state.publicKeyBase64,
    sessionName: state.sessionName,
    peerId: state.node.peerId.toString(),
    voice: state.voiceRoom === roomId,
    id: crypto.randomUUID(),
    timestamp: Date.now(),
  })
}

function getRoomHistoryKey(roomId) {
  return `${ROOM_HISTORY_PREFIX}${roomId}`
}

function getRoomLastSeenKey(roomId) {
  return `${ROOM_LAST_SEEN_PREFIX}${roomId}`
}

function getRoomPassphraseKey(roomId) {
  return `${ROOM_PASSPHRASE_PREFIX}${roomId}`
}

function persistRoomsState() {
  const rooms = []
  for (const [roomId, room] of state.rooms) {
    rooms.push({
      roomId,
      roomName: room.roomName,
      hasPassphrase: !!room.hasPassphrase,
      lastSeen: Number(room.lastSeen) || Date.now(),
    })
  }
  const payload = {
    rooms,
    currentRoomId: state.currentRoomId || null,
  }
  try {
    localStorage.setItem(ROOMS_STATE_KEY, JSON.stringify(payload))
  } catch {
    // ignore storage failures
  }
}

function restoreRoomsState() {
  try {
    const raw = localStorage.getItem(ROOMS_STATE_KEY)
    if (!raw) return
    const json = JSON.parse(raw)
    if (!json || !Array.isArray(json.rooms)) return
    for (const item of json.rooms) {
      if (!item || typeof item !== 'object') continue
      const roomId = typeof item.roomId === 'string' ? item.roomId : ''
      const roomName = typeof item.roomName === 'string' ? item.roomName : ''
      if (!roomId || !roomName) continue
      if (!state.rooms.has(roomId)) {
        state.rooms.set(roomId, {
          roomId,
          roomName,
          hasPassphrase: !!item.hasPassphrase,
          members: new Set(),
          lastSeen: Number(item.lastSeen) || Date.now(),
        })
      }
      const storedPass = sessionStorage.getItem(getRoomPassphraseKey(roomId))
      if (storedPass) state.roomPassphrases.set(roomId, storedPass)
    }
    const candidateRoomId = typeof json.currentRoomId === 'string' ? json.currentRoomId : ''
    if (candidateRoomId && state.rooms.has(candidateRoomId)) {
      state.currentRoomId = candidateRoomId
    }
  } catch {
    // ignore storage failures
  }
}

function saveRoomPassphrase(roomId, passphrase) {
  if (!roomId || !passphrase) return
  try {
    sessionStorage.setItem(getRoomPassphraseKey(roomId), passphrase)
  } catch {
    // ignore storage failures
  }
}

async function restoreJoinedRoomsAfterConnect() {
  const joinedRooms = [...state.joined]
  for (const roomId of joinedRooms) {
    const room = state.rooms.get(roomId)
    if (!room) continue
    if (room.hasPassphrase && !state.roomKeys.has(roomId)) {
      const pass = state.roomPassphrases.get(roomId)
      if (pass) {
        try {
          const key = await deriveRoomKey(roomId, pass)
          state.roomKeys.set(roomId, key)
        } catch {
          continue
        }
      } else {
        continue
      }
    }
    await joinRoomTopic(roomId, true)
  }
}

function saveRoomLastSeen(roomId, timestamp = Date.now()) {
  state.roomLastSeen.set(roomId, timestamp)
  try {
    sessionStorage.setItem(getRoomLastSeenKey(roomId), String(timestamp))
  } catch {
    // ignore storage failures
  }
}

function loadRoomLastSeen(roomId) {
  if (state.roomLastSeen.has(roomId)) return state.roomLastSeen.get(roomId)
  try {
    const raw = sessionStorage.getItem(getRoomLastSeenKey(roomId))
    const value = Number(raw)
    if (Number.isFinite(value) && value > 0) {
      state.roomLastSeen.set(roomId, value)
      return value
    }
  } catch {
    // ignore storage failures
  }
  return 0
}

function saveRoomHistory(roomId) {
  const list = (state.messages.get(roomId) || []).slice(-SESSION_HISTORY_LIMIT)
  const serializable = list.map((m) => ({
    id: m.id,
    kind: m.kind || 'chat',
    fromPublicKey: m.fromPublicKey,
    fromName: m.fromName,
    toPublicKey: m.toPublicKey || null,
    timestamp: m.timestamp,
    encrypted: !!m.encrypted,
    text: typeof m.text === 'string' ? m.text : null,
    raw: typeof m.raw === 'string' ? m.raw : null,
  }))
  try {
    sessionStorage.setItem(getRoomHistoryKey(roomId), JSON.stringify(serializable))
  } catch {
    // ignore storage failures
  }
}

function loadRoomHistory(roomId) {
  if ((state.messages.get(roomId) || []).length) return
  try {
    const raw = sessionStorage.getItem(getRoomHistoryKey(roomId))
    if (!raw) return
    const list = JSON.parse(raw)
    if (!Array.isArray(list)) return
    const sanitized = list
      .filter((m) => m && typeof m === 'object')
      .map((m) => ({
        id: String(m.id || crypto.randomUUID()),
        kind: m.kind === 'dm' ? 'dm' : 'chat',
        fromPublicKey: String(m.fromPublicKey || ''),
        fromName: String(m.fromName || ''),
        toPublicKey: m.toPublicKey ? String(m.toPublicKey) : null,
        timestamp: Number(m.timestamp) || Date.now(),
        encrypted: !!m.encrypted,
        text: typeof m.text === 'string' ? m.text : null,
        raw: typeof m.raw === 'string' ? m.raw : null,
      }))
    state.messages.set(roomId, sanitized)
  } catch {
    // ignore storage failures
  }
}

function formatMessageTimestamp(timestamp) {
  const date = new Date(timestamp || Date.now())
  const now = new Date()
  const sameDay = date.toDateString() === now.toDateString()
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (sameDay) return time
  const day = date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  return `${day} ${time}`
}

function appendRoomEntry(roomId, entry) {
  const list = state.messages.get(roomId) || []
  if (entry.id && list.some((m) => m.id === entry.id)) return null
  list.push(entry)
  state.messages.set(roomId, list.slice(-SESSION_HISTORY_LIMIT))
  saveRoomHistory(roomId)
  return entry
}

function updateReconnectButton() {
  const btn = document.getElementById('reconnect-btn')
  if (!btn) return
  const visible = !!state.node || state.isReconnecting
  btn.style.display = visible ? 'inline-block' : 'none'
  btn.disabled = state.isReconnecting
  btn.textContent = state.isReconnecting ? 'Reconnecting…' : 'Reconnect'
}

function startHeartbeatLoops() {
  if (state.announceIntervalId) clearInterval(state.announceIntervalId)
  if (state.statusIntervalId) clearInterval(state.statusIntervalId)
  state.announceIntervalId = setInterval(() => {
    announceMyRooms().catch(console.error)
    requestRoomDiscoverySync().catch(console.error)
    for (const roomId of state.joined) {
      publishRoomPresence(roomId).catch(console.error)
    }
  }, 10000)
  state.statusIntervalId = setInterval(() => {
    const roomsChanged = prunePresence()
    if (roomsChanged) {
      renderRooms()
    }
    updateStatus()
    renderVoicePanel()
    if (!state.isReconnecting && state.node && state.isOnline && state.hadPeerConnection && state.node.getPeers().length === 0) {
      scheduleReconnect('peer mesh dropped to zero')
      return
    }
    if (!state.isReconnecting && state.node && state.isOnline) {
      const peers = state.node.getPeers().length
      const staleFor = Date.now() - (state.lastRemoteDiscoveryAt || 0)
      if (peers > 0 && staleFor > DISCOVERY_STALE_TIMEOUT_MS) {
        state.discoveryStaleChecks += 1
        requestRoomDiscoverySync().catch(console.error)
        if (state.discoveryStaleChecks >= DISCOVERY_STALE_MAX_CONSECUTIVE) {
          scheduleReconnect(`discovery responses stale for ${staleFor}ms with ${peers} peers`)
        }
      } else {
        state.discoveryStaleChecks = 0
      }
    }
  }, 5000)
}

function scheduleReconnect(reason) {
  if (state.isReconnecting || state.reconnectTimerId) return
  console.warn('Scheduling reconnect:', reason)
  state.reconnectTimerId = setTimeout(() => {
    state.reconnectTimerId = null
    reconnectNode().catch(console.error)
  }, 1200)
}

async function deriveDirectKey(remotePublicKeyB64) {
  const cached = state.directKeys.get(remotePublicKeyB64)
  if (cached) return cached
  const remotePublicKey = await crypto.subtle.importKey(
    'spki',
    fromB64(remotePublicKeyB64),
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    [],
  )
  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: remotePublicKey },
    state.keyPair.privateKey,
    256,
  )
  const key = await crypto.subtle.importKey(
    'raw',
    sharedBits,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  )
  state.directKeys.set(remotePublicKeyB64, key)
  return key
}

async function encryptDirectMessage(toPublicKeyB64, text) {
  const key = await deriveDirectKey(toPublicKeyB64)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const pt = new TextEncoder().encode(text)
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, pt)
  const buf = new Uint8Array(iv.length + ct.byteLength)
  buf.set(iv, 0)
  buf.set(new Uint8Array(ct), iv.length)
  return b64(buf)
}

async function decryptDirectMessage(fromPublicKeyB64, payload) {
  const key = await deriveDirectKey(fromPublicKeyB64)
  const buf = fromB64(payload)
  const iv = buf.slice(0, 12)
  const ct = buf.slice(12)
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct)
  return new TextDecoder().decode(pt)
}

function normalizeAlias(value) {
  return (value || '').trim().replace(/^@/, '').toLowerCase()
}

async function resolveBootstrapAlias(alias) {
  const cached = state.bootstrapAliases.get(alias)
  if (cached) return cached
  const url = `${BOOTSTRAP_RESOLVE_URL}?alias=${encodeURIComponent(alias)}`
  const res = await fetch(url, { cache: 'no-store' })
  if (res.status === 404) {
    throw new Error(`Unknown bootstrap alias: ${alias}`)
  }
  if (!res.ok) {
    throw new Error(`Could not resolve bootstrap alias: ${alias}`)
  }
  const json = await res.json()
  const address = normalizeBootstrapAddr(typeof json?.address === 'string' ? json.address.trim() : '')
  if (!address) throw new Error(`Bootstrap alias returned no address: ${alias}`)
  state.bootstrapAliases.set(alias, address)
  return address
}

async function resolveBootstrapEntry(entry) {
  const value = entry.trim()
  if (!value) return null
  if (value.startsWith('/')) return normalizeBootstrapAddr(value)

  if (value.includes('/')) {
    return normalizeBootstrapAddr(value)
  }

  const alias = normalizeAlias(value)
  if (alias) return resolveBootstrapAlias(alias)

  throw new Error(`Unknown bootstrap alias: ${value}`)
}

function b64(u) {
  const bytes = u instanceof Uint8Array ? u : new Uint8Array(u)
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s)
}

function fromB64(s) {
  const str = atob(s)
  const arr = new Uint8Array(str.length)
  for (let i = 0; i < str.length; i++) arr[i] = str.charCodeAt(i)
  return arr
}

async function deriveRoomKey(roomId, passphrase) {
  const enc = new TextEncoder()
  const base = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey'],
  )
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: enc.encode(roomId),
      iterations: 100000,
      hash: 'SHA-256',
    },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

async function encryptRoomMessage(roomId, passphrase, text) {
  const key = state.roomKeys.get(roomId) || (await deriveRoomKey(roomId, passphrase))
  if (!state.roomKeys.has(roomId)) state.roomKeys.set(roomId, key)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const pt = new TextEncoder().encode(text)
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, pt)
  const buf = new Uint8Array(iv.length + ct.byteLength)
  buf.set(iv, 0)
  buf.set(new Uint8Array(ct), iv.length)
  return b64(buf)
}

async function decryptRoomMessage(roomId, payload) {
  const key = state.roomKeys.get(roomId)
  if (!key) return null
  const buf = fromB64(payload)
  const iv = buf.slice(0, 12)
  const ct = buf.slice(12)
  try {
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct)
    return new TextDecoder().decode(pt)
  } catch {
    return null
  }
}

async function generateIdentity() {
  state.keyPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveBits'],
  )
  const exported = await crypto.subtle.exportKey('spki', state.keyPair.publicKey)
  const raw = new Uint8Array(exported)
  state.publicKeyBase64 = b64(raw)
  state.sessionName = state.publicKeyBase64.slice(0, 16)
  state.peerNames.set(state.publicKeyBase64, state.sessionName)
}

async function startNode() {
  const raw = document.getElementById('bootstraps').value.trim()
  const entries = raw.split('\n').map((line) => line.trim()).filter(Boolean)
  const list = []
  for (const entry of entries) {
    const resolved = await resolveBootstrapEntry(entry)
    if (resolved) list.push(resolved)
  }
  if (list.some((addr) => addr.includes('<peer-id>'))) {
    throw new Error('Bootstrap address still contains <peer-id> placeholder')
  }
  if (list.some((addr) => addr.includes('REPLACE_WITH_CURRENT_RELAY_PEER_ID'))) {
    throw new Error('Bootstrap alias still contains REPLACE_WITH_CURRENT_RELAY_PEER_ID placeholder')
  }
  const discoverers = list.length ? [bootstrap({ list })] : []
  const host = globalThis.location?.hostname || ''
  const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '::1'

  state.node = await createLibp2p({
    transports: [webSockets()],
    connectionEncrypters: [noise()],
    streamMuxers: [yamux()],
    peerDiscovery: discoverers,
    ...(isLocalHost
      ? {
          connectionGater: {
            denyDialMultiaddr: () => false,
          },
        }
      : {}),
    services: {
      identify: identify(),
      pubsub: gossipsub({ allowPublishToZeroTopicPeers: true }),
    },
  })

  await state.node.start()
  await state.node.services.pubsub.subscribe(DISCOVERY_TOPIC)
  state.node.services.pubsub.addEventListener('message', onPubsubMessage)
  state.node.addEventListener('peer:connect', () => {
    state.hadPeerConnection = true
    updateStatus()
    announceMyRooms().catch(console.error)
    requestRoomDiscoverySync().catch(console.error)
  })
  state.node.addEventListener('peer:disconnect', updateStatus)

  if (list.length) {
    const dialResults = await Promise.allSettled(
      list.map(async (addr) => state.node.dial(multiaddr(addr))),
    )
    const connected = dialResults.some((r) => r.status === 'fulfilled')
    dialResults.forEach((result, i) => {
      if (result.status === 'rejected') {
        console.error(`dial failed for ${list[i]}:`, result.reason)
      }
    })
    if (!connected) {
      await state.node.stop()
      state.node = null
      throw new Error('Could not dial any bootstrap address')
    }
  }

  state.isOnline = true
  state.isReconnecting = false
  updateStatus()
  updateReconnectButton()
  announceMyRooms()
  requestRoomDiscoverySync().catch(console.error)
}

async function reconnectNode() {
  if (state.isReconnecting) return
  state.isReconnecting = true
  updateStatus()
  updateReconnectButton()
  const joinedRooms = [...state.joined]
  const selectedRoomId = state.currentRoomId
  const voiceRoomBeforeReconnect = state.voiceRoom
  if (state.voiceRoom) leaveVoice()
  try {
    if (state.node) {
      await state.node.stop().catch(console.error)
      state.node = null
      state.isOnline = false
    }
    await startNode()
    state.lastRemoteDiscoveryAt = Date.now()
    state.discoveryStaleChecks = 0
    for (const roomId of joinedRooms) {
      await joinRoomTopic(roomId, true)
    }
    if (selectedRoomId && state.rooms.has(selectedRoomId) && state.joined.has(selectedRoomId)) {
      selectRoom(selectedRoomId)
    }
    if (voiceRoomBeforeReconnect && state.joined.has(voiceRoomBeforeReconnect)) {
      if (state.currentRoomId !== voiceRoomBeforeReconnect) {
        selectRoom(voiceRoomBeforeReconnect)
      }
      await toggleVoice()
    }
  } catch (err) {
    console.error('Reconnect failed:', err)
    alert(`Reconnect failed: ${err?.message || 'try again'}`)
  } finally {
    state.isReconnecting = false
    updateStatus()
    updateReconnectButton()
  }
}

function updateStatus() {
  const dot = document.getElementById('status-dot')
  const text = document.getElementById('status-text')
  const peers = state.node ? state.node.getPeers().length : 0
  const online = state.isOnline && !!state.node
  const degraded = online && peers === 0
  dot.classList.toggle('online', online && !degraded)
  dot.classList.toggle('degraded', degraded || state.isReconnecting)
  text.textContent = state.isReconnecting
    ? 'reconnecting…'
    : online
      ? degraded
        ? 'online'
        : 'online'
      : 'offline'
  updateConnectButton()
  updateReconnectButton()
}

function onPubsubMessage(event) {
  const { detail } = event
  const { topic, data } = detail
  let msg
  try {
    msg = JSON.parse(new TextDecoder().decode(data))
  } catch {
    return
  }
  if (!msg || typeof msg !== 'object') return
  if (msg.publicKey === state.publicKeyBase64) return
  if (msg.id && state.seen.has(msg.id)) return
  if (msg.id) state.seen.add(msg.id)

  if (topic === DISCOVERY_TOPIC) handleDiscoveryMessage(msg)
  else if (topic.startsWith(ROOM_PREFIX)) handleRoomMessage(topic.slice(ROOM_PREFIX.length), msg)
}

async function requestRoomDiscoverySync() {
  if (!state.node) return
  await publish(DISCOVERY_TOPIC, {
    type: 'discovery-request',
    publicKey: state.publicKeyBase64,
    sessionName: state.sessionName,
    peerId: state.node.peerId.toString(),
    id: crypto.randomUUID(),
    timestamp: Date.now(),
  })
}

function fromHistoryEntry(entry) {
  if (!entry || typeof entry !== 'object') return null
  if (entry.type !== 'chat' && entry.type !== 'direct-chat') return null
  if (!entry.id || !entry.payload) return null
  return {
    id: entry.id,
    type: entry.type,
    fromPublicKey: entry.fromPublicKey,
    fromName: entry.fromName,
    to: entry.to || null,
    timestamp: entry.timestamp,
    encrypted: !!entry.encrypted,
    payload: entry.payload,
  }
}

async function sendHistorySince(roomId, toPublicKey, sinceTimestamp) {
  const list = state.messages.get(roomId) || []
  const entries = list
    .filter((m) => Number(m.timestamp) > Number(sinceTimestamp || 0))
    .map((m) => ({
      id: m.id,
      type: m.kind === 'dm' ? 'direct-chat' : 'chat',
      fromPublicKey: m.fromPublicKey,
      fromName: m.fromName,
      to: m.toPublicKey || null,
      timestamp: m.timestamp,
      encrypted: !!m.encrypted,
      payload: m.raw,
    }))
    .filter((m) => !!m.payload)
    .slice(-HISTORY_SYNC_LIMIT)
  if (!entries.length) return
  await publish(ROOM_PREFIX + roomId, {
    type: 'history-batch',
    roomId,
    to: toPublicKey,
    fromPublicKey: state.publicKeyBase64,
    fromName: state.sessionName,
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    entries,
  })
}

async function ingestRoomChat(roomId, msg) {
  if (!msg.id) return
  const entry = appendRoomEntry(roomId, {
    id: msg.id,
    kind: 'chat',
    fromPublicKey: msg.fromPublicKey,
    fromName: msg.fromName,
    timestamp: msg.timestamp,
    encrypted: !!msg.encrypted,
    text: null,
    raw: msg.payload,
  })
  if (!entry) return
  if (entry.encrypted) {
    decryptRoomMessage(roomId, msg.payload).then((text) => {
      if (text) {
        entry.text = text
        saveRoomHistory(roomId)
        if (state.currentRoomId === roomId) renderMessages()
      }
    })
  } else {
    entry.text = new TextDecoder().decode(fromB64(msg.payload))
    saveRoomHistory(roomId)
  }
  saveRoomLastSeen(roomId, Number(entry.timestamp) || Date.now())
  if (state.currentRoomId === roomId) renderMessages()
}

async function ingestDirectMessage(roomId, msg) {
  if (!msg.id) return
  const entry = appendRoomEntry(roomId, {
    id: msg.id,
    kind: 'dm',
    fromPublicKey: msg.fromPublicKey,
    fromName: msg.fromName,
    toPublicKey: msg.to,
    timestamp: msg.timestamp,
    encrypted: true,
    text: null,
    raw: msg.payload,
  })
  if (!entry) return
  try {
    entry.text = await decryptDirectMessage(msg.fromPublicKey, msg.payload)
  } catch {
    entry.text = null
  }
  saveRoomHistory(roomId)
  saveRoomLastSeen(roomId, Number(entry.timestamp) || Date.now())
  if (state.currentRoomId === roomId) renderMessages()
}

function handleDiscoveryMessage(msg) {
  state.lastRemoteDiscoveryAt = Date.now()
  state.discoveryStaleChecks = 0
  if (msg.publicKey && msg.sessionName) {
    setPeerName(msg.publicKey, msg.sessionName)
  }
  if (msg.type === 'discovery-request') {
    announceMyRooms().catch(console.error)
  } else if (msg.type === 'announce' && msg.roomId && msg.roomName) {
    upsertRoom(msg.roomId, msg.roomName, !!msg.hasPassphrase, msg.publicKey)
  } else if (msg.type === 'rooms' && Array.isArray(msg.rooms)) {
    for (const r of msg.rooms) {
      if (r.roomId && r.roomName) {
        upsertRoom(r.roomId, r.roomName, !!r.hasPassphrase, msg.publicKey)
      }
    }
  }
  persistRoomsState()
  renderRooms()
}

function upsertRoom(roomId, roomName, hasPassphrase, publisherKey) {
  const existing = state.rooms.get(roomId)
  const members = new Set(existing?.members || [])
  if (publisherKey) members.add(publisherKey)
  state.rooms.set(roomId, {
    roomId,
    roomName,
    hasPassphrase,
    members,
    lastSeen: Date.now(),
  })
  persistRoomsState()
}

function handleRoomMessage(roomId, msg) {
  if (msg.publicKey && msg.sessionName) {
    setPeerName(msg.publicKey, msg.sessionName)
    touchRoomPresence(roomId, msg.publicKey, { sessionName: msg.sessionName })
  }
  if (msg.fromPublicKey && msg.fromName) {
    setPeerName(msg.fromPublicKey, msg.fromName)
    touchRoomPresence(roomId, msg.fromPublicKey, { sessionName: msg.fromName })
  }
  if (msg.type === 'presence' && msg.publicKey) {
    touchRoomPresence(roomId, msg.publicKey, {
      sessionName: msg.sessionName,
      voice: !!msg.voice,
    })
    if (state.voiceRoom === roomId && msg.voice && msg.publicKey !== state.publicKeyBase64) {
      if (!state.voicePeers.has(msg.publicKey) && shouldInitiateVoiceOffer(msg.publicKey)) {
        createVoiceOffer(roomId, msg.publicKey).catch(console.error)
      }
    }
    renderVoicePanel()
  } else if (msg.type === 'voice-join' && msg.publicKey) {
    touchRoomPresence(roomId, msg.publicKey, { sessionName: msg.sessionName, voice: true })
    if (state.voiceRoom === roomId && msg.publicKey !== state.publicKeyBase64) {
      if (shouldInitiateVoiceOffer(msg.publicKey)) {
        createVoiceOffer(roomId, msg.publicKey).catch(console.error)
      }
    }
    renderVoicePanel()
  } else if (msg.type === 'voice-leave' && msg.publicKey) {
    touchRoomPresence(roomId, msg.publicKey, { sessionName: msg.sessionName, voice: false })
    closeVoicePeer(msg.publicKey)
    renderVoicePanel()
  } else if (msg.type === 'voice-signal' && msg.to === state.publicKeyBase64) {
    if (msg.from !== state.publicKeyBase64) {
      handleVoiceSignal(roomId, msg.from, msg.signal).catch(console.error)
    }
  } else if (msg.type === 'chat' && msg.payload) {
    ingestRoomChat(roomId, msg).catch(console.error)
  } else if (msg.type === 'direct-chat' && msg.payload && msg.to === state.publicKeyBase64) {
    ingestDirectMessage(roomId, msg).catch(console.error)
  } else if (msg.type === 'history-request' && msg.fromPublicKey && msg.fromPublicKey !== state.publicKeyBase64) {
    if (state.joined.has(roomId)) {
      sendHistorySince(roomId, msg.fromPublicKey, msg.since).catch(console.error)
    }
  } else if (msg.type === 'history-batch' && msg.to === state.publicKeyBase64 && Array.isArray(msg.entries)) {
    for (const rawEntry of msg.entries) {
      const entry = fromHistoryEntry(rawEntry)
      if (!entry) continue
      if (entry.type === 'chat') ingestRoomChat(roomId, entry).catch(console.error)
      if (entry.type === 'direct-chat' && entry.to === state.publicKeyBase64) {
        ingestDirectMessage(roomId, entry).catch(console.error)
      }
    }
  } else if (msg.type === 'join' && msg.publicKey) {
    touchRoomPresence(roomId, msg.publicKey, { sessionName: msg.sessionName })
    const r = state.rooms.get(roomId)
    if (r) {
      r.members.add(msg.publicKey)
      renderRooms()
    }
    if (msg.publicKey !== state.publicKeyBase64 && state.joined.has(roomId)) {
      sendHistorySince(roomId, msg.publicKey, msg.since || 0).catch(console.error)
    }
  }
}

async function publish(topic, data) {
  if (!state.node) return
  try {
    await state.node.services.pubsub.publish(
      topic,
      new TextEncoder().encode(JSON.stringify(data)),
    )
  } catch (err) {
    console.error('publish error on', topic, err)
  }
}

async function announceMyRooms() {
  if (!state.node) return
  for (const roomId of state.joined) {
    touchRoomPresence(roomId, state.publicKeyBase64, {
      sessionName: state.sessionName,
      voice: state.voiceRoom === roomId,
    })
  }
  const rooms = []
  for (const [roomId, r] of state.rooms) {
    if (state.joined.has(roomId)) {
      rooms.push({
        roomId,
        roomName: r.roomName,
        hasPassphrase: r.hasPassphrase,
      })
    }
  }
  await publish(DISCOVERY_TOPIC, {
    type: 'rooms',
    rooms,
    publicKey: state.publicKeyBase64,
    sessionName: state.sessionName,
    peerId: state.node.peerId.toString(),
    id: crypto.randomUUID(),
    timestamp: Date.now(),
  })
}

function renderIdentity() {
  const input = document.getElementById('session-name-input')
  if (input) input.value = state.sessionName
}

function updateConnectButton() {
  const btn = document.getElementById('connect-btn')
  if (!btn) return
  const peers = state.node ? state.node.getPeers().length : 0
  const connected = state.isOnline && !state.isReconnecting && peers > 0
  const connecting = !connected && (state.isReconnecting || (state.isOnline && !!state.node && peers === 0))
  btn.textContent = connected ? 'Connected' : (connecting ? 'Connecting...' : 'Connect')
  btn.disabled = connected || connecting
  btn.classList.toggle('connected', connected)
  btn.classList.toggle('connecting', connecting)
}

async function saveSessionName() {
  const input = document.getElementById('session-name-input')
  if (!input) return
  const next = normalizeSessionName(input.value)
  if (!next) {
    input.value = state.sessionName
    return
  }
  state.sessionName = next
  state.peerNames.set(state.publicKeyBase64, state.sessionName)
  renderIdentity()
  renderMessages()
  if (!state.node) return
  await announceMyRooms()
  if (state.currentRoomId && state.joined.has(state.currentRoomId)) {
    await publish(ROOM_PREFIX + state.currentRoomId, {
      type: 'join',
      roomId: state.currentRoomId,
      publicKey: state.publicKeyBase64,
      sessionName: state.sessionName,
      peerId: state.node.peerId.toString(),
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    })
  }
}

function renderRooms() {
  const el = document.getElementById('rooms-list')
  el.innerHTML = ''
  prunePresence()
  if (state.currentRoomId && !state.rooms.has(state.currentRoomId)) {
    state.currentRoomId = null
    const nameEl = document.getElementById('chat-room-name')
    const encryptedEl = document.getElementById('chat-encrypted')
    const inviteBtn = document.getElementById('invite-btn')
    const dmBtn = document.getElementById('dm-btn')
    if (nameEl) nameEl.textContent = 'Select or create a room'
    if (encryptedEl) encryptedEl.style.display = 'none'
    if (inviteBtn) inviteBtn.disabled = true
    if (dmBtn) dmBtn.disabled = true
    renderMessages()
    updateVoiceButton()
    renderVoicePanel()
    persistRoomsState()
  }
  for (const [, r] of state.rooms) {
    const div = document.createElement('div')
    div.className = `room-item ${state.currentRoomId === r.roomId ? 'active' : ''}`
    div.innerHTML = `
      <div class="name">${escapeHtml(r.roomName)} ${r.hasPassphrase ? '🔒' : ''}</div>
      <div class="meta">${r.members.size} online</div>
    `
    div.onclick = () => selectRoom(r.roomId)
    el.appendChild(div)
  }
}

function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

function lobbyIntroMarkup() {
  return `
    <div class="empty">Create or join a room to start chatting.</div>
    <div class="chat-intro">
      <h3>Owl Chat</h3>
      <ul>
        <li>no sign-ups, no personal information used or stored</li>
        <li>group chat with voice</li>
        <li>rooms disappear when the last user leaves</li>
        <li>private, passphrase protected rooms are encrypted</li>
        <li>use DM to share passphrase with other online users for private rooms</li>
        <li>Firefox private tab on mobile keeps voice chat open even when mobile is locked</li>
      </ul>
      <p class="chat-intro-break"></p>
      <p>
        alias for network connection is rotated, email
        <a href="mailto:contact@decentralogix.com">contact@decentralogix.com</a>
        if you need the current alias
      </p>
      <p class="chat-intro-break"></p>
      <ul>
        <li>set session name to anything, or ignore</li>
        <li>set alias in network block, or use IPFS libp2p multiaddr</li>
        <li>connect</li>
      </ul>
    </div>
  `
}

function selectRoom(roomId) {
  const room = state.rooms.get(roomId)
  if (!room) return
  loadRoomHistory(roomId)
  if (state.voiceRoom && state.voiceRoom !== roomId) {
    leaveVoice()
  }
  if (!state.joined.has(roomId)) {
    showJoinRoom(roomId)
    return
  }
  state.currentRoomId = roomId
  document.getElementById('chat-room-name').textContent = room.roomName
  document.getElementById('chat-encrypted').style.display = room.hasPassphrase ? 'inline' : 'none'
  document.getElementById('invite-btn').disabled = false
  document.getElementById('dm-btn').disabled = false
  renderRooms()
  renderMessages()
  updateVoiceButton()
  renderVoicePanel()
  saveRoomLastSeen(roomId)
  persistRoomsState()
}

function renderMessages() {
  const el = document.getElementById('messages-list')
  el.innerHTML = ''
  if (!state.currentRoomId) {
    el.innerHTML = lobbyIntroMarkup()
    return
  }
  const list = (state.messages.get(state.currentRoomId) || []).slice(-100)
  if (!list.length) {
    el.innerHTML = '<div class="empty">No messages yet.</div>'
    return
  }
  for (const m of list) {
    const own = m.fromPublicKey === state.publicKeyBase64
    const fromName = getDisplayName(m.fromPublicKey, m.fromName)
    const body = m.text !== null ? escapeHtml(m.text) : '<span class="encrypted">[encrypted message]</span>'
    const dmLabel = m.kind === 'dm'
      ? `<span class="dm-tag">DM${m.toPublicKey && m.fromPublicKey === state.publicKeyBase64 ? ' → ' + escapeHtml(getDisplayName(m.toPublicKey)) : ''}</span>`
      : ''
    const div = document.createElement('div')
    div.className = `message ${own ? 'own' : ''}`
    div.innerHTML = `
      <div class="message-header">
        <span>${escapeHtml(fromName)} ${dmLabel}</span>
        <span>${formatMessageTimestamp(m.timestamp)}</span>
      </div>
      <div class="message-body">${body}</div>
    `
    el.appendChild(div)
  }
  el.scrollTop = el.scrollHeight
}

function showCreateRoom() {
  const backdrop = document.createElement('div')
  backdrop.className = 'modal-backdrop'
  backdrop.id = 'create-modal'
  backdrop.innerHTML = `
    <div class="modal">
      <h2>Create Room</h2>
      <input id="new-room-name" placeholder="Room name" />
      <input id="new-room-pass" type="password" placeholder="Passphrase for encryption (leave blank for open room)" />
      <div class="hint">The passphrase is never transmitted. Share it out-of-band or in the URL fragment.</div>
      <button type="button" id="confirm-create">Create</button>
      <button type="button" class="secondary" id="cancel-create">Cancel</button>
    </div>
  `
  document.body.appendChild(backdrop)
  document.getElementById('cancel-create').onclick = () => backdrop.remove()
  document.getElementById('confirm-create').onclick = async () => {
    const nameInput = document.getElementById('new-room-name')
    const passInput = document.getElementById('new-room-pass')
    const name = nameInput.value.trim()
    const pass = passInput.value
    if (!name) return

    const roomId = crypto.randomUUID()
    state.rooms.set(roomId, { roomId, roomName: name, hasPassphrase: !!pass, members: new Set([state.publicKeyBase64]) })
    state.joined.add(roomId)
    touchRoomPresence(roomId, state.publicKeyBase64, { sessionName: state.sessionName, voice: false })
    if (pass) {
      state.roomKeys.set(roomId, await deriveRoomKey(roomId, pass))
      state.roomPassphrases.set(roomId, pass)
      saveRoomPassphrase(roomId, pass)
    }
    persistRoomsState()

    selectRoom(roomId)
    backdrop.remove()

    try {
      state.node.services.pubsub.subscribe(ROOM_PREFIX + roomId)
      await publish(DISCOVERY_TOPIC, {
        type: 'announce',
        roomId,
        roomName: name,
        hasPassphrase: !!pass,
        publicKey: state.publicKeyBase64,
        sessionName: state.sessionName,
        peerId: state.node.peerId.toString(),
        id: crypto.randomUUID(),
        timestamp: Date.now(),
      })
      await announceMyRooms()
    } catch (err) {
      console.error('Create room network error:', err)
    }
  }
}

async function showJoinRoom(roomId) {
  const room = state.rooms.get(roomId)
  if (!room) return
  const needsPass = room.hasPassphrase && !state.roomKeys.has(roomId)
  if (!needsPass) {
    try {
      await joinRoomTopic(roomId)
      selectRoom(roomId)
    } catch (err) {
      console.error('Join room error:', err)
      alert('Could not join room. See console.')
    }
    return
  }
  const backdrop = document.createElement('div')
  backdrop.className = 'modal-backdrop'
  backdrop.id = 'join-modal'
  backdrop.innerHTML = `
    <div class="modal">
      <h2>Join ${escapeHtml(room.roomName)}</h2>
      <input id="join-room-pass" type="password" placeholder="Room passphrase" autofocus />
      <button type="button" id="confirm-join">Join</button>
      <button type="button" class="secondary" id="cancel-join">Cancel</button>
    </div>
  `
  document.body.appendChild(backdrop)
  document.getElementById('cancel-join').onclick = () => backdrop.remove()
  document.getElementById('confirm-join').onclick = async () => {
    const pass = document.getElementById('join-room-pass').value
    if (!pass) return
    try {
      const key = await deriveRoomKey(roomId, pass)
      state.roomKeys.set(roomId, key)
      state.roomPassphrases.set(roomId, pass)
      saveRoomPassphrase(roomId, pass)
      await joinRoomTopic(roomId)
      selectRoom(roomId)
      backdrop.remove()
    } catch (err) {
      console.error('Join room error:', err)
      alert('Could not join room; check the passphrase and console.')
    }
  }
}

async function joinRoomTopic(roomId, isReconnect = false) {
  state.joined.add(roomId)
  touchRoomPresence(roomId, state.publicKeyBase64, {
    sessionName: state.sessionName,
    voice: state.voiceRoom === roomId,
  })
  await state.node.services.pubsub.subscribe(ROOM_PREFIX + roomId)
  const since = loadRoomLastSeen(roomId)
  await publish(ROOM_PREFIX + roomId, {
    type: 'join',
    roomId,
    publicKey: state.publicKeyBase64,
    sessionName: state.sessionName,
    peerId: state.node.peerId.toString(),
    since,
    reconnect: !!isReconnect,
    id: crypto.randomUUID(),
    timestamp: Date.now(),
  })
  await publish(ROOM_PREFIX + roomId, {
    type: 'history-request',
    roomId,
    fromPublicKey: state.publicKeyBase64,
    fromName: state.sessionName,
    since,
    id: crypto.randomUUID(),
    timestamp: Date.now(),
  })
  await publishRoomPresence(roomId)
  await announceMyRooms()
  persistRoomsState()
}

function getRoomMembersForDm(roomId) {
  return getActiveRoomMembers(roomId)
    .filter((key) => key && key !== state.publicKeyBase64)
    .map((key) => ({ key, name: getDisplayName(key) }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

async function sendDirectMessage(roomId, toPublicKey, text) {
  const payload = await encryptDirectMessage(toPublicKey, text)
  const msg = {
    type: 'direct-chat',
    id: crypto.randomUUID(),
    roomId,
    to: toPublicKey,
    fromPublicKey: state.publicKeyBase64,
    fromName: state.sessionName,
    timestamp: Date.now(),
    payload,
    encrypted: true,
  }
  state.seen.add(msg.id)
  await publish(ROOM_PREFIX + roomId, msg)
  appendRoomEntry(roomId, {
    id: msg.id,
    kind: 'dm',
    fromPublicKey: msg.fromPublicKey,
    fromName: msg.fromName,
    toPublicKey,
    timestamp: msg.timestamp,
    encrypted: true,
    text,
    raw: payload,
  })
  if (state.currentRoomId === roomId) renderMessages()
}

function showDirectMessageModal() {
  const roomId = state.currentRoomId
  if (!roomId || !state.joined.has(roomId)) return
  const members = getRoomMembersForDm(roomId)
  if (!members.length) {
    alert('No members available for DM yet. Ask them to join the room first.')
    return
  }
  const backdrop = document.createElement('div')
  backdrop.className = 'modal-backdrop'
  backdrop.id = 'dm-modal'
  backdrop.innerHTML = `
    <div class="modal">
      <h2>Send direct message</h2>
      <select id="dm-target">
        ${members.map((m) => `<option value="${escapeHtml(m.key)}">${escapeHtml(m.name)}</option>`).join('')}
      </select>
      <textarea id="dm-text" placeholder="Message (only recipient can decrypt)"></textarea>
      <button type="button" id="confirm-dm">Send DM</button>
      <button type="button" class="secondary" id="cancel-dm">Cancel</button>
    </div>
  `
  document.body.appendChild(backdrop)
  document.getElementById('cancel-dm').onclick = () => backdrop.remove()
  document.getElementById('confirm-dm').onclick = async () => {
    const toPublicKey = document.getElementById('dm-target').value
    const text = document.getElementById('dm-text').value.trim()
    if (!toPublicKey || !text) return
    try {
      await sendDirectMessage(roomId, toPublicKey, text)
      backdrop.remove()
    } catch (err) {
      console.error('DM send failed:', err)
      alert('Could not send DM. See console for details.')
    }
  }
}

async function sendMessage(e) {
  e.preventDefault()
  if (!state.currentRoomId || !state.node) return
  const input = document.getElementById('message-input')
  const text = input.value.trim()
  if (!text) return
  input.value = ''

  const room = state.rooms.get(state.currentRoomId)
  const hasPass = room?.hasPassphrase
  let payload = text
  let encrypted = false
  if (hasPass) {
    payload = await encryptRoomMessage(state.currentRoomId, null, text)
    encrypted = true
  } else {
    payload = b64(new TextEncoder().encode(text))
  }

  const msg = {
    type: 'chat',
    id: crypto.randomUUID(),
    roomId: state.currentRoomId,
    fromPublicKey: state.publicKeyBase64,
    fromName: state.sessionName,
    timestamp: Date.now(),
    payload,
    encrypted,
  }
  await publish(ROOM_PREFIX + state.currentRoomId, msg)
  state.seen.add(msg.id)

  appendRoomEntry(state.currentRoomId, {
    id: msg.id,
    kind: 'chat',
    fromPublicKey: state.publicKeyBase64,
    fromName: state.sessionName,
    timestamp: msg.timestamp,
    encrypted,
    text,
    raw: payload,
  })
  renderMessages()
  saveRoomLastSeen(state.currentRoomId)
}

const DEFAULT_ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }]

function shouldInitiateVoiceOffer(remotePublicKey) {
  return state.publicKeyBase64 < remotePublicKey
}

function showAudioContainer(visible) {
  const container = document.getElementById('audio-container')
  if (!container) return
  container.style.display = visible ? 'block' : 'none'
}

async function tryPlayRemoteAudio(el) {
  if (!el) return
  try {
    await el.play()
    return
  } catch {
    el.controls = true
    el.dataset.needsTap = '1'
    alert('Tap the remote audio player once to start voice on this device.')
  }
}

function updateVoiceButton() {
  const btn = document.getElementById('voice-btn')
  if (!btn) return
  const can = state.isOnline && state.currentRoomId && state.joined.has(state.currentRoomId)
  if (state.voiceRoom === state.currentRoomId) {
    btn.textContent = 'Leave voice'
    btn.classList.add('danger')
  } else {
    btn.textContent = 'Join voice'
    btn.classList.remove('danger')
  }
  btn.disabled = !can
}

async function toggleVoice() {
  if (state.voiceRoom) {
    leaveVoice()
    return
  }
  const roomId = state.currentRoomId
  if (!roomId || !state.joined.has(roomId) || !state.node) return
  try {
    state.localStream = await navigator.mediaDevices.getUserMedia({ audio: true })
  } catch {
    alert('Microphone access is required for voice chat.')
    return
  }
  state.voiceRoom = roomId
  touchRoomPresence(roomId, state.publicKeyBase64, { voice: true, sessionName: state.sessionName })
  showAudioContainer(true)
  createAudioMeter(state.publicKeyBase64, state.localStream)
  updateVoiceButton()
  renderVoicePanel()
  try {
    await publish(ROOM_PREFIX + roomId, {
      type: 'voice-join',
      roomId,
      publicKey: state.publicKeyBase64,
      sessionName: state.sessionName,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    })
  } catch (err) {
    console.error('voice join publish failed', err)
  }
  await publishRoomPresence(roomId)
}

function leaveVoice() {
  const roomId = state.voiceRoom
  state.voiceRoom = null
  if (state.localStream) {
    for (const track of state.localStream.getTracks()) track.stop()
    state.localStream = null
  }
  for (const [key, pc] of state.voicePeers) {
    pc.close()
  }
  state.voicePeers.clear()
  for (const key of state.voiceMeters.keys()) {
    stopAudioMeter(key)
  }
  state.voicePendingCandidates.clear()
  const container = document.getElementById('audio-container')
  if (container) container.innerHTML = ''
  showAudioContainer(false)
  updateVoiceButton()
  renderVoicePanel()
  if (roomId && state.node) {
    touchRoomPresence(roomId, state.publicKeyBase64, { voice: false, sessionName: state.sessionName })
    publish(ROOM_PREFIX + roomId, {
      type: 'voice-leave',
      roomId,
      publicKey: state.publicKeyBase64,
      sessionName: state.sessionName,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    }).catch(console.error)
    publishRoomPresence(roomId).catch(console.error)
  }
}

function closeVoicePeer(publicKey) {
  const pc = state.voicePeers.get(publicKey)
  if (!pc) return
  pc.close()
  state.voicePeers.delete(publicKey)
  stopAudioMeter(publicKey)
  const el = document.getElementById('audio-' + publicKey)
  if (el) el.remove()
  renderVoicePanel()
}

function createAudioMeter(publicKey, stream) {
  if (!publicKey || !stream) return
  stopAudioMeter(publicKey)
  const AudioCtx = globalThis.AudioContext || globalThis.webkitAudioContext
  if (!AudioCtx) return
  try {
    const ctx = new AudioCtx()
    const source = ctx.createMediaStreamSource(stream)
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 256
    analyser.smoothingTimeConstant = 0.8
    source.connect(analyser)
    const bins = new Uint8Array(analyser.frequencyBinCount)
    let rafId = 0
    const tick = () => {
      analyser.getByteFrequencyData(bins)
      let total = 0
      for (let i = 0; i < bins.length; i += 1) total += bins[i]
      const avg = total / bins.length
      if (avg > 18) {
        markSpeaking(publicKey)
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    state.voiceMeters.set(publicKey, { ctx, source, analyser, rafId })
  } catch (err) {
    console.warn('Audio meter unavailable for peer', publicKey, err)
  }
}

function stopAudioMeter(publicKey) {
  const meter = state.voiceMeters.get(publicKey)
  if (!meter) return
  try {
    cancelAnimationFrame(meter.rafId)
    meter.source.disconnect()
    meter.analyser.disconnect()
    meter.ctx.close().catch(() => {})
  } catch {
    // ignore meter cleanup failures
  }
  state.voiceMeters.delete(publicKey)
  state.voiceSpeakingUntil.delete(publicKey)
}

function getVoiceParticipants(roomId) {
  const now = Date.now()
  const map = state.roomPresence.get(roomId)
  const list = []
  if (map) {
    for (const [publicKey, entry] of map) {
      if (!entry?.voice) continue
      if (!entry?.lastSeen || now - entry.lastSeen > ROOM_PRESENCE_TTL_MS) continue
      list.push(publicKey)
    }
  }
  if (state.voiceRoom === roomId && state.publicKeyBase64) {
    if (!list.includes(state.publicKeyBase64)) list.push(state.publicKeyBase64)
  }
  return list.sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)))
}

function renderVoicePanel() {
  const panel = document.getElementById('voice-panel')
  const list = document.getElementById('voice-participants')
  if (!panel || !list) return
  const roomId = state.currentRoomId
  if (!roomId) {
    list.innerHTML = '<div class="empty">Select a room to view voice participants.</div>'
    return
  }
  const now = Date.now()
  const participants = getVoiceParticipants(roomId)
  if (state.voiceRoom !== roomId && !participants.length) {
    list.innerHTML = '<div class="empty">Join voice to see participants.</div>'
    return
  }
  if (!participants.length) {
    list.innerHTML = '<div class="empty">No one in voice yet.</div>'
    return
  }
  list.innerHTML = participants.map((publicKey) => {
    const name = getDisplayName(publicKey)
    const self = publicKey === state.publicKeyBase64
    const speaking = isSpeaking(publicKey, now)
    const status = speaking ? 'speaking' : 'connected'
    const statusText = speaking ? 'Speaking' : 'Connected'
    return `<div class="voice-user-row ${self ? 'self' : ''}">
      <span class="voice-dot ${status}"></span>
      <span class="voice-user-name">${escapeHtml(name)}${self ? ' (you)' : ''}</span>
      <span class="voice-user-state">${statusText}</span>
    </div>`
  }).join('')
}

function createPeerConnection(publicKey) {
  const pc = new RTCPeerConnection({
    iceServers: state.iceServers.length ? state.iceServers : DEFAULT_ICE_SERVERS,
  })
  pc.ontrack = (event) => {
    let el = document.getElementById('audio-' + publicKey)
    if (!el) {
      el = document.createElement('audio')
      el.id = 'audio-' + publicKey
      el.autoplay = true
      el.playsInline = true
      el.controls = false
      document.getElementById('audio-container').appendChild(el)
    }
    el.srcObject = event.streams[0]
    createAudioMeter(publicKey, event.streams[0])
    tryPlayRemoteAudio(el).catch(console.error)
    renderVoicePanel()
  }
  pc.onicecandidate = (event) => {
    if (!event.candidate || !state.voiceRoom) return
    publish(ROOM_PREFIX + state.voiceRoom, {
      type: 'voice-signal',
      to: publicKey,
      from: state.publicKeyBase64,
      signal: { type: 'candidate', candidate: event.candidate.toJSON() },
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    }).catch(console.error)
  }
  pc.onconnectionstatechange = () => {
    if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
      closeVoicePeer(publicKey)
    }
    renderVoicePanel()
  }
  if (state.localStream) {
    for (const track of state.localStream.getTracks()) {
      pc.addTrack(track, state.localStream)
    }
  }
  return pc
}

async function createVoiceOffer(roomId, toPublicKey) {
  if (state.voiceRoom !== roomId) return
  if (state.voicePeers.has(toPublicKey)) {
    closeVoicePeer(toPublicKey)
  }
  const pc = createPeerConnection(toPublicKey)
  state.voicePeers.set(toPublicKey, pc)
  await pc.setLocalDescription(await pc.createOffer())
  publish(ROOM_PREFIX + roomId, {
    type: 'voice-signal',
    to: toPublicKey,
    from: state.publicKeyBase64,
    signal: { type: 'offer', sdp: pc.localDescription.sdp },
    id: crypto.randomUUID(),
    timestamp: Date.now(),
  }).catch(console.error)
}

async function handleVoiceSignal(roomId, fromPublicKey, signal) {
  if (state.voiceRoom !== roomId) return
  if (signal.type === 'offer') {
    let pc = state.voicePeers.get(fromPublicKey)
    if (pc) pc.close()
    pc = createPeerConnection(fromPublicKey)
    state.voicePeers.set(fromPublicKey, pc)
    state.voicePendingCandidates.set(fromPublicKey, [])
    await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: signal.sdp }))
    await pc.setLocalDescription(await pc.createAnswer())
    publish(ROOM_PREFIX + roomId, {
      type: 'voice-signal',
      to: fromPublicKey,
      from: state.publicKeyBase64,
      signal: { type: 'answer', sdp: pc.localDescription.sdp },
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    }).catch(console.error)
  } else if (signal.type === 'answer') {
    const pc = state.voicePeers.get(fromPublicKey)
    if (!pc) return
    await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: signal.sdp }))
    const pending = state.voicePendingCandidates.get(fromPublicKey) || []
    for (const candidate of pending) {
      await pc.addIceCandidate(new RTCIceCandidate(candidate))
    }
    state.voicePendingCandidates.delete(fromPublicKey)
  } else if (signal.type === 'candidate') {
    const pc = state.voicePeers.get(fromPublicKey)
    if (!pc) return
    if (!pc.remoteDescription) {
      const pending = state.voicePendingCandidates.get(fromPublicKey) || []
      pending.push(signal.candidate)
      state.voicePendingCandidates.set(fromPublicKey, pending)
      return
    }
    await pc.addIceCandidate(new RTCIceCandidate(signal.candidate))
  }
}

function buildApp() {
  const app = document.getElementById('app')
  app.innerHTML = `
    <div class="app">
      <aside class="sidebar">
        <div>
          <h1>Owl Chat</h1>
          <div class="status">
            <span id="status-dot" class="status-dot"></span>
            <span id="status-text">offline</span>
            <button id="reconnect-btn" class="secondary" style="display:none;padding:4px 8px">Reconnect</button>
          </div>
        </div>
        <div class="identity">
          <div class="label">Session name</div>
          <div class="identity-name-row">
            <input id="session-name-input" maxlength="32" placeholder="Your display name" />
            <button id="save-session-name" class="secondary">Save</button>
          </div>
        </div>
        <div class="section">
          <h3>Network</h3>
          <select id="bootstrap-preset">
            <option value="custom">Custom multiaddr</option>
            <option value="local">Local test relay (127.0.0.1:8080)</option>
          </select>
          <textarea id="bootstraps" class="bootstrap-input" placeholder="Paste the full multiaddr from your relay here…"></textarea>
          <button id="connect-btn">Connect</button>
        </div>
        <div class="section">
          <div style="display:flex;justify-content:space-between;align-items:center">
            <h3>Available Rooms</h3>
            <button id="create-room-btn" class="secondary" style="padding:4px 8px">+ New</button>
          </div>
          <div id="rooms-list" class="rooms"></div>
        </div>
        <div class="section">
          <h3>Voice Participants</h3>
          <aside id="voice-panel" class="voice-panel">
            <div id="voice-participants" class="voice-participants">
              <div class="empty">Select a room to view voice participants.</div>
            </div>
          </aside>
        </div>
      </aside>
      <main class="main">
        <div class="chat-header">
          <div class="chat-name">
            <h2 id="chat-room-name">Select or create a room</h2>
            <span id="chat-encrypted" class="encrypted-tag" style="display:none">Encrypted</span>
          </div>
          <div class="chat-actions">
            <button id="voice-btn" class="secondary" disabled>Join voice</button>
            <button id="dm-btn" class="secondary" disabled>DM</button>
            <button id="invite-btn" class="secondary" disabled>Copy invite</button>
          </div>
        </div>
        <div id="messages-layout" class="messages-layout">
          <div id="messages-list" class="messages">
            ${lobbyIntroMarkup()}
          </div>
        </div>
        <div id="audio-container" style="display:none"></div>
        <form id="composer" class="composer">
          <input id="message-input" autocomplete="off" placeholder="Type a message…" disabled />
          <button type="submit" id="send-btn" disabled>Send</button>
        </form>
      </main>
    </div>
  `
}

async function copyInvite() {
  if (!state.currentRoomId) return
  const room = state.rooms.get(state.currentRoomId)
  const pass = state.roomPassphrases.get(state.currentRoomId) || ''
  const base = window.location.href.split('#')[0].split('?')[0]
  const parts = [`room=${encodeURIComponent(state.currentRoomId)}`, `name=${encodeURIComponent(room.roomName)}`]
  if (pass) parts.push(`pass=${encodeURIComponent(pass)}`)
  const url = `${base}#${parts.join('&')}`
  await navigator.clipboard.writeText(url)
  alert('Invite link copied. The passphrase is in the URL fragment and is not sent to any server.')
}

async function tryAutoJoinFromHash() {
  const params = new URLSearchParams(window.location.hash.slice(1))
  const roomId = params.get('room')
  const name = params.get('name')
  const pass = params.get('pass')
  if (!roomId) return
  if (!state.rooms.has(roomId) && name) {
    state.rooms.set(roomId, { roomId, roomName: name, hasPassphrase: !!pass, members: new Set() })
  }
  if (!state.rooms.has(roomId)) return
  if (pass) {
    try {
      const key = await deriveRoomKey(roomId, pass)
      state.roomKeys.set(roomId, key)
      state.roomPassphrases.set(roomId, pass)
    } catch {
      alert('Could not derive room key from URL fragment')
    }
  }
  await joinRoomTopic(roomId)
  selectRoom(roomId)
}

async function init() {
  buildApp()
  restoreRoomsState()
  await loadIceServers()
  await generateIdentity()
  renderIdentity()

  const preset = document.getElementById('bootstrap-preset')
  const ta = document.getElementById('bootstraps')

  function applyPreset() {
    if (preset.value === 'local') {
      ta.value = '/ip4/127.0.0.1/tcp/8080/ws/p2p/<peer-id>'
      ta.focus()
      ta.placeholder = 'Enter alias or libp2p multiaddr'
    } else {
      ta.value = ''
      ta.placeholder = 'Enter alias or libp2p multiaddr'
    }
  }

  preset.onchange = applyPreset
  applyPreset()

  document.getElementById('connect-btn').onclick = async () => {
    document.getElementById('connect-btn').disabled = true
    try {
      await startNode()
      state.lastRemoteDiscoveryAt = Date.now()
      state.discoveryStaleChecks = 0
      await restoreJoinedRoomsAfterConnect()
      await tryAutoJoinFromHash()
      if (state.currentRoomId && state.rooms.has(state.currentRoomId) && state.joined.has(state.currentRoomId)) {
        selectRoom(state.currentRoomId)
      }
      startHeartbeatLoops()
      document.getElementById('message-input').disabled = false
      document.getElementById('send-btn').disabled = false
      document.getElementById('create-room-btn').disabled = false
      document.getElementById('invite-btn').disabled = false
      document.getElementById('dm-btn').disabled = !state.currentRoomId
      updateVoiceButton()
    } catch (err) {
      console.error(err)
      alert(`Failed to start libp2p node: ${err?.message || 'Check the bootstrap addresses.'}`)
    } finally {
      document.getElementById('connect-btn').disabled = false
    }
  }

  document.getElementById('create-room-btn').onclick = showCreateRoom
  document.getElementById('composer').onsubmit = sendMessage
  document.getElementById('invite-btn').onclick = copyInvite
  document.getElementById('voice-btn').onclick = toggleVoice
  document.getElementById('dm-btn').onclick = showDirectMessageModal
  document.getElementById('reconnect-btn').onclick = () => reconnectNode().catch(console.error)
  document.getElementById('save-session-name').onclick = () => saveSessionName().catch(console.error)
  document.getElementById('session-name-input').onkeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      saveSessionName().catch(console.error)
    }
  }

  document.getElementById('create-room-btn').disabled = true
  document.getElementById('dm-btn').disabled = true
  document.getElementById('invite-btn').disabled = true
  document.getElementById('send-btn').disabled = true
  document.getElementById('message-input').disabled = true
  updateConnectButton()
  renderRooms()
  if (state.voicePanelIntervalId) clearInterval(state.voicePanelIntervalId)
  state.voicePanelIntervalId = setInterval(() => renderVoicePanel(), 500)
  window.addEventListener('online', () => scheduleReconnect('browser came online'))
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && state.node && state.node.getPeers().length === 0) {
      scheduleReconnect('tab became visible without peers')
    }
  })
  window.addEventListener('pageshow', () => {
    if (state.node && state.node.getPeers().length === 0) {
      scheduleReconnect('page show without peers')
    }
  })
  updateReconnectButton()
  updateVoiceButton()
}

init()
